import { useState, useRef, useCallback, useEffect } from 'react';
import { FOCUSABLE_SELECTORS } from '../../constants/focusableSelectors.js';

// How long a burst of single-character keypresses is treated as one typeahead
// query before it resets, e.g. typing "u" then "s" quickly jumps to "US".
const TYPEAHEAD_RESET_MS = 700;
const focusableSelectors = FOCUSABLE_SELECTORS.join(', ');
/**
 * @description Shared open/close, roving-focus keyboard navigation (arrow keys, Home/End,
 * typeahead), and outside-click/escape handling for a button-triggered listbox dropdown
 * rendered in a portal (e.g. PhoneInput's country picker, Select). Owns no rendering — callers
 * wire the returned refs/handlers onto their own trigger button and `<li role="option">` markup.
 * @param {UseListboxDropdownParams} params - Item count, selection state, and selection callback
 * @return {UseListboxDropdownResult} Open state, active index, refs, and event handlers
 */
function useListboxDropdown({ itemCount, selectedIndex, getItemText, isItemDisabled, onSelect, }) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndexState] = useState(selectedIndex >= 0 ? selectedIndex : 0);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const optionRefs = useRef([]);
    const typeaheadRef = useRef({ query: '', lastTime: 0 });
    const isDisabled = useCallback((index) => isItemDisabled?.(index) ?? false, [isItemDisabled]);
    // Walks forward/backward from `start`, wrapping, until it lands on a non-disabled
    // option. Falls back to `start` itself if every option is disabled.
    const findEnabledIndex = useCallback((start, direction) => {
        if (itemCount === 0)
            return -1;
        let index = start;
        for (let i = 0; i < itemCount; i++) {
            index = (index + direction + itemCount) % itemCount;
            if (!isDisabled(index))
                return index;
        }
        return isDisabled(start) ? -1 : start;
    }, [itemCount, isDisabled]);
    const setActiveIndex = useCallback((index) => {
        if (index < 0 || index >= itemCount || isDisabled(index))
            return;
        setActiveIndexState(index);
    }, [itemCount, isDisabled]);
    const focusOption = useCallback((index) => {
        optionRefs.current[index]?.focus();
    }, []);
    const open = useCallback(() => {
        setIsOpen(true);
        const seedIndex = selectedIndex >= 0 && !isDisabled(selectedIndex) ? selectedIndex : findEnabledIndex(-1, 1);
        setActiveIndexState(seedIndex < 0 ? 0 : seedIndex);
    }, [selectedIndex, isDisabled, findEnabledIndex]);
    const close = useCallback((focusTrigger = true) => {
        setIsOpen(false);
        if (focusTrigger)
            triggerRef.current?.focus();
    }, []);
    const toggle = useCallback(() => {
        if (isOpen) {
            close();
        }
        else {
            open();
        }
    }, [isOpen, open, close]);
    const selectIndex = useCallback((index) => {
        if (index < 0 || index >= itemCount || isDisabled(index))
            return;
        onSelect(index);
        close();
    }, [itemCount, isDisabled, onSelect, close]);
    // Moves real DOM focus onto the active option whenever the list opens or the active
    // index changes, following the WAI-ARIA "Listbox with Button" pattern. This gives the
    // active option native :focus / :focus-visible styling and screen-reader announcement
    // for free, instead of simulating it with aria-activedescendant.
    useEffect(() => {
        if (!isOpen)
            return;
        focusOption(activeIndex);
    }, [isOpen, activeIndex, focusOption]);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleClickOutside = (event) => {
            const target = event.target;
            if (!containerRef.current?.contains(target) && !listRef.current?.contains(target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);
    const handleTypeahead = useCallback((key) => {
        const now = Date.now();
        const isContinuation = now - typeaheadRef.current.lastTime < TYPEAHEAD_RESET_MS;
        const query = (isContinuation ? typeaheadRef.current.query : '') + key.toLowerCase();
        typeaheadRef.current = { query, lastTime: now };
        const startIndex = isContinuation ? activeIndex : (activeIndex + 1) % itemCount;
        for (let i = 0; i < itemCount; i++) {
            const index = (startIndex + i) % itemCount;
            if (isDisabled(index))
                continue;
            if (getItemText(index).toLowerCase().startsWith(query)) {
                setActiveIndex(index);
                return;
            }
        }
    }, [activeIndex, itemCount, isDisabled, getItemText, setActiveIndex]);
    const handleTriggerKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                event.preventDefault();
                if (!isOpen) {
                    open();
                }
                else {
                    setActiveIndex(findEnabledIndex(activeIndex, event.key === 'ArrowDown' ? 1 : -1));
                }
                break;
            }
            case 'Enter':
            case ' ': {
                if (!isOpen) {
                    event.preventDefault();
                    open();
                }
                break;
            }
            case 'Escape': {
                if (isOpen) {
                    event.preventDefault();
                    close();
                }
                break;
            }
        }
    }, [isOpen, open, close, activeIndex, findEnabledIndex, setActiveIndex]);
    const handleOptionKeyDown = useCallback((event, index) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex(findEnabledIndex(index, 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex(findEnabledIndex(index, -1));
                break;
            case 'Home':
                event.preventDefault();
                setActiveIndex(isDisabled(0) ? findEnabledIndex(0, 1) : 0);
                break;
            case 'End': {
                event.preventDefault();
                const lastIndex = itemCount - 1;
                setActiveIndex(isDisabled(lastIndex) ? findEnabledIndex(lastIndex, -1) : lastIndex);
                break;
            }
            case 'Enter':
            case ' ':
                event.preventDefault();
                selectIndex(index);
                break;
            case 'Escape':
                event.preventDefault();
                close();
                break;
            case 'Tab': {
                // The list is portaled to the end of <body>, so its DOM position doesn't match
                // the trigger's place in the page. Close first, then manually move focus to
                // whatever element actually comes before/after the trigger, instead of letting
                // native Tab order jump to wherever the portal happens to live in the DOM.
                // The option elements themselves are excluded from consideration: at this point
                // React hasn't re-rendered yet, so the currently active option's tabindex="0"
                // is still in the DOM and would otherwise be (mis)treated as "the next stop".
                close(false);
                const focusable = Array.from(document.querySelectorAll(focusableSelectors)).filter((el) => !listRef.current?.contains(el));
                const triggerIndex = triggerRef.current ? focusable.indexOf(triggerRef.current) : -1;
                if (triggerIndex === -1)
                    break;
                const nextIndex = event.shiftKey ? triggerIndex - 1 : triggerIndex + 1;
                const nextElement = focusable[nextIndex];
                if (nextElement) {
                    event.preventDefault();
                    nextElement.focus();
                }
                break;
            }
            default:
                if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
                    event.preventDefault();
                    handleTypeahead(event.key);
                }
                break;
        }
    }, [findEnabledIndex, isDisabled, itemCount, setActiveIndex, selectIndex, close, handleTypeahead]);
    const registerOptionRef = useCallback((index) => (node) => {
        optionRefs.current[index] = node;
    }, []);
    return {
        isOpen,
        activeIndex,
        containerRef,
        triggerRef,
        listRef,
        open,
        close,
        toggle,
        setActiveIndex,
        selectIndex,
        handleTriggerKeyDown,
        handleOptionKeyDown,
        registerOptionRef,
    };
}

export { useListboxDropdown };
